import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiUpload } from '../../../helpers/apiRequester'
import { BatchWidgetCard, BatchWidgetStep } from '../../reusable/batchWidgetCard/BatchWidgetCard'
import { Banner, Button, Card, CodeBlock, CollapsibleSection, EmptyState, KeyValueList, Spinner, StatusPill } from '../../../ui'
import { ClaimSearch } from '../commsInboxView/ClaimSearch'
import { formatDateTime, humanise } from '../commsInboxView/shared'
import {
    BATCH_STATUS,
    CaseCandidate,
    EXTRACTION_SOURCE,
    ITEM_STATUS,
    IngestBatch,
    IngestBatchDetail,
    IngestItem,
    IngestItemDetail,
    PROCESSING_REASON,
    ProcessingStatus,
    REPROCESSABLE_STATUSES,
    UploadResult,
    batchIsActive,
    formatBytes,
    isInFlight,
} from './shared'
import styles from './styles.module.scss'

const POLL_INTERVAL_MS = 4000

const errorMessage = (err: any, fallback: string): string => err?.response?.data?.message || fallback

const ItemStatusPill = ({ item }: { item: Pick<IngestItem, 'status'> }) => (
    <StatusPill size="sm" tone={ITEM_STATUS[item.status].tone} dot>
        {ITEM_STATUS[item.status].label}
    </StatusPill>
)

const SourcePill = ({ item }: { item: Pick<IngestItem, 'extraction_source'> }) => {
    if (!item.extraction_source) return <span className={styles.muted}>—</span>
    const source = EXTRACTION_SOURCE[item.extraction_source]
    return (
        <StatusPill size="sm" tone={source.tone} title={source.hint}>
            {source.label}
        </StatusPill>
    )
}

/** What the case column says. Anything short of a successful confirmation is "suggested". */
const caseSummary = (item: IngestItem) => {
    if (item.confirmed_claim_ref) return <span className={styles.mono}>Filed on {item.confirmed_claim_ref}</span>
    const top = item.candidates_json?.[0]
    if (!top) return <span className={styles.muted}>{item.status === 'review' ? 'No suggestion — search to choose' : '—'}</span>
    const others = (item.candidates_json?.length ?? 1) - 1
    return (
        <span>
            <span className={styles.suggestedTag}>Suggested</span> <span className={styles.mono}>{top.claimRef}</span>
            {others > 0 && <span className={styles.muted}> +{others} more</span>}
        </span>
    )
}

const describeCandidate = (c: CaseCandidate) => [c.clientName || 'Unnamed client', c.lenderName, c.introducer].filter(Boolean).join(' · ')

// ─── Review workspace ─────────────────────────────────────────────────────

const ReviewWorkspace = ({ listItem, onChanged }: { listItem: IngestItem; onChanged: () => Promise<void> }) => {
    const [item, setItem] = useState<IngestItemDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [sourceUrl, setSourceUrl] = useState<string | null>(null)
    const [sourceError, setSourceError] = useState<string | null>(null)
    const [selectedClaim, setSelectedClaim] = useState<{ claimRef: string; from: 'suggestion' | 'search' } | null>(null)
    const [busy, setBusy] = useState<'confirm' | 'retry' | 'reprocess' | 'download' | null>(null)

    const itemId = listItem.id

    const loadItem = useCallback(async () => {
        try {
            const resp = await api({ url: 'comms/document-ingest/item', data: { itemId } })
            if (resp.data?.success) setItem(resp.data.item)
        } catch (err: any) {
            Report.failure('Load failed', errorMessage(err, 'Could not load this letter.'), 'Okay')
        } finally {
            setLoading(false)
        }
    }, [itemId])

    const loadSource = useCallback(async () => {
        setSourceError(null)
        try {
            const resp = await api({ url: 'comms/document-ingest/source', data: { itemId } })
            if (resp.data?.success) setSourceUrl(resp.data.url)
        } catch (err: any) {
            setSourceUrl(null)
            setSourceError(errorMessage(err, 'Could not prepare the original document.'))
        }
    }, [itemId])

    useEffect(() => {
        loadItem()
        loadSource()
    }, [loadItem, loadSource])

    // The batch poll carries the latest status; reload the detail (and the filed copy) when it moves.
    const listStamp = `${listItem.status}|${listItem.last_status_at}`
    const loadedStamp = item ? `${item.status}|${item.last_status_at}` : null
    useEffect(() => {
        if (!item || loadedStamp === listStamp) return
        const wasConfirmed = item.status === 'confirmed'
        loadItem()
        if (!wasConfirmed && listItem.status === 'confirmed') loadSource()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listStamp, loadedStamp])

    const handleDownload = async () => {
        setBusy('download')
        try {
            const resp = await api({ url: 'comms/document-ingest/source', data: { itemId, download: true } })
            if (resp.data?.success) {
                const opened = window.open(resp.data.url, '_blank')
                if (!opened) Report.info('Pop-up blocked', 'Allow pop-ups for this site to download the original.', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Download failed', errorMessage(err, 'Could not prepare the original document.'), 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const handleRetry = async () => {
        setBusy('retry')
        try {
            await api({ url: 'comms/document-ingest/retry', data: { itemId } })
            await Promise.all([loadItem(), onChanged()])
        } catch (err: any) {
            Report.failure('Retry failed', errorMessage(err, 'Could not retry this letter.'), 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const handleReprocess = () => {
        Confirm.show(
            'Read this letter again?',
            'The saved text, summary, category and suggested cases for this letter are discarded and it is read again from the original PDF (scanned pages go through OCR again). Use this only when the saved reading is wrong.',
            'Read again',
            'Cancel',
            async () => {
                setBusy('reprocess')
                try {
                    await api({ url: 'comms/document-ingest/reprocess', data: { itemId } })
                    setSelectedClaim(null)
                    await Promise.all([loadItem(), onChanged()])
                } catch (err: any) {
                    Report.failure('Could not read again', errorMessage(err, 'Could not reprocess this letter.'), 'Okay')
                } finally {
                    setBusy(null)
                }
            },
            () => undefined
        )
    }

    const handleConfirm = () => {
        if (!item || !selectedClaim) return
        const claimRef = selectedClaim.claimRef
        Confirm.show(
            'Confirm case and file',
            `File "${item.original_filename}" on claim ${claimRef}? The original PDF and its saved text and summary are copied into that claim's documents. The generated LOC on the claim is not changed.`,
            'Confirm and file',
            'Cancel',
            async () => {
                setBusy('confirm')
                try {
                    const resp = await api({ url: 'comms/document-ingest/confirm-case', data: { itemId, claimRef } })
                    if (resp.data?.success) {
                        Report.success(
                            resp.data.alreadyConfirmed ? 'Already filed' : 'Filed',
                            `This letter is filed on claim ${resp.data.claimRef}.`,
                            'Okay'
                        )
                    }
                } catch (err: any) {
                    Report.failure('Not filed', `${errorMessage(err, 'Could not confirm the case.')} Nothing was filed.`, 'Okay')
                } finally {
                    setSelectedClaim(null)
                    await Promise.all([loadItem(), loadSource(), onChanged()])
                    setBusy(null)
                }
            },
            () => undefined
        )
    }

    if (loading || !item) return <Spinner block text="Loading letter…" />

    const result = item.result_json
    const candidates = item.candidates_json || []
    const pages = item.pages_json || []
    const ocrPages = pages.filter((p) => p.source === 'ocr').length
    const unreadablePages = pages.filter((p) => p.error).map((p) => p.page)
    const confirmed = item.status === 'confirmed'
    const reviewable = item.status === 'review'
    const canReprocess = !confirmed && REPROCESSABLE_STATUSES.includes(item.status)
    const valueOf = (field?: { value: string | null } | null) => field?.value || '—'
    const listOf = (values?: string[]) => (values && values.length ? values.join(', ') : '—')
    const clientName = result ? [result.client.firstName.value, result.client.lastName.value].filter(Boolean).join(' ') : ''

    return (
        <Card
            as="section"
            title={item.original_filename}
            subtitle={
                <span className={styles.detailSub}>
                    <StatusPill size="sm" tone="outline">
                        LOC
                    </StatusPill>
                    <ItemStatusPill item={item} />
                    <span>{formatBytes(item.size_bytes)}</span>
                    <span>Uploaded {formatDateTime(item.created_at)}</span>
                    {item.last_status_by && (
                        <span>
                            Last change by {item.last_status_by} · {formatDateTime(item.last_status_at)}
                        </span>
                    )}
                </span>
            }
            actions={
                <>
                    <Button size="sm" onClick={handleDownload} loading={busy === 'download'} disabled={busy !== null}>
                        Download original
                    </Button>
                    {canReprocess && (
                        <Button size="sm" variant="ghost" onClick={handleReprocess} loading={busy === 'reprocess'} disabled={busy !== null} title="Discard the saved reading and read the original again">
                            Read again
                        </Button>
                    )}
                </>
            }
        >
            <div className={styles.review}>
                <div className={styles.previewColumn}>
                    {sourceUrl && item.mime_type?.startsWith('image/') ? (
                        <img className={styles.previewImage} src={sourceUrl} alt={item.original_filename} />
                    ) : sourceUrl ? (
                        <iframe className={styles.preview} src={sourceUrl} title={`Original: ${item.original_filename}`} />
                    ) : sourceError ? (
                        <EmptyState
                            tone="error"
                            title="Preview unavailable"
                            description={sourceError}
                            action={
                                <Button size="sm" onClick={loadSource}>
                                    Try again
                                </Button>
                            }
                        />
                    ) : (
                        <Spinner block text="Preparing original…" />
                    )}
                    <p className={styles.note}>{confirmed ? 'Showing the filed copy on the claim.' : 'Showing the original as uploaded. Nothing is filed until the case is confirmed.'}</p>
                </div>

                <div className={styles.resultColumn}>
                    {isInFlight(item.status) && (
                        <Banner tone="info" title={ITEM_STATUS[item.status].label}>
                            This letter is still being processed. This page updates by itself; you can leave and come back.
                        </Banner>
                    )}

                    {item.status === 'failed' && (
                        <Banner
                            tone="bad"
                            title={`Processing failed${item.attempt_count > 1 ? ` (attempt ${item.attempt_count})` : ''}`}
                            actions={
                                <Button size="sm" onClick={handleRetry} loading={busy === 'retry'} disabled={busy !== null}>
                                    Retry
                                </Button>
                            }
                        >
                            {item.last_error || 'The letter could not be processed.'} The original is safe and can still be downloaded.
                        </Banner>
                    )}

                    {item.status !== 'failed' && item.last_error && (
                        <Banner tone="warn" title="Partly unreadable">
                            {item.last_error}
                        </Banner>
                    )}

                    {confirmed && (
                        <Banner tone="good" title={`Filed on claim ${item.confirmed_claim_ref}`}>
                            Confirmed by {item.confirmed_by || 'unknown'} on {formatDateTime(item.confirmed_at)}
                            {item.filed_introducer ? ` · ${item.filed_introducer}` : ''}.
                            {item.filed_pdf_key && <div className={styles.filedKey}>{item.filed_pdf_key}</div>}
                        </Banner>
                    )}

                    {item.extracted_at && (
                        <section className={styles.block}>
                            <h4 className={styles.blockTitle}>Summary</h4>
                            <p className={styles.summary}>{item.summary || (item.extraction_source === 'unreadable' ? 'No summary — no text could be read from this letter.' : '—')}</p>
                            <KeyValueList
                                stacked
                                items={[
                                    { label: 'Category', value: item.category_label || item.category_key || '—' },
                                    {
                                        label: 'Case action',
                                        value: item.next_action_label ? (
                                            <span>
                                                <strong>{item.next_action_label}</strong>
                                                {item.suggested_next_action && <span className={styles.actionDetail}>{item.suggested_next_action}</span>}
                                            </span>
                                        ) : (
                                            item.suggested_next_action || '—'
                                        ),
                                    },
                                ]}
                            />
                            {item.category_key && <p className={styles.note}>Category and case action as set in Categories when this letter was processed ({formatDateTime(item.summarised_at)}).</p>}
                        </section>
                    )}

                    {item.extracted_at && (
                        <section className={styles.block}>
                            <h4 className={styles.blockTitle}>Reading</h4>
                            <KeyValueList
                                columns
                                items={[
                                    { label: 'Read by', value: <SourcePill item={item} /> },
                                    { label: 'Pages', value: item.page_count ? `${item.page_count}${ocrPages ? ` (${ocrPages} by OCR)` : ''}` : '—' },
                                    { label: 'Unreadable pages', value: unreadablePages.length ? unreadablePages.join(', ') : 'None' },
                                    { label: 'OCR model', value: item.ocr_model || '—', mono: true },
                                ]}
                            />
                        </section>
                    )}

                    {result && (
                        <section className={styles.block}>
                            <h4 className={styles.blockTitle}>Details found in the letter</h4>
                            <KeyValueList
                                columns
                                items={[
                                    { label: 'Lender', value: valueOf(result.lenderName) },
                                    { label: 'Letter date', value: valueOf(result.dates.letterDate) },
                                    { label: 'Deadline', value: valueOf(result.dates.deadlineDate) },
                                    { label: 'Client', value: clientName || '—' },
                                    { label: 'Postcode', value: valueOf(result.client.postcode) },
                                    { label: 'Date of birth', value: valueOf(result.client.dateOfBirth) },
                                    { label: 'Vehicle', value: valueOf(result.vehicle.registration), mono: true },
                                    { label: 'Our claim refs', value: listOf(result.identifiers.ourClaimRefs), mono: true },
                                    { label: 'Proclaim refs', value: listOf(result.identifiers.proclaimRefs), mono: true },
                                    { label: 'Lender refs', value: listOf(result.identifiers.lenderCaseRefs), mono: true },
                                    { label: 'Agreement numbers', value: listOf(result.identifiers.agreementNumbers), mono: true },
                                ]}
                            />
                        </section>
                    )}

                    {reviewable && (
                        <section className={styles.block}>
                            <h4 className={styles.blockTitle}>Case</h4>
                            <p className={styles.note}>These are suggestions only. The letter is not linked to any claim until you select a case and confirm it.</p>
                            {candidates.length === 0 ? (
                                <p className={styles.muted}>No case was suggested. Search below to choose one.</p>
                            ) : (
                                <ul className={styles.candidateList}>
                                    {candidates.map((c) => {
                                        const isSelected = selectedClaim?.claimRef === c.claimRef
                                        return (
                                            <li key={c.claimRef} className={`${styles.candidate} ${isSelected ? styles.candidateSelected : ''}`}>
                                                <div className={styles.candidateBody}>
                                                    <div className={styles.candidateTop}>
                                                        <span className={styles.suggestedTag}>{c.preselected ? 'Suggested · strong match' : 'Suggested'}</span>
                                                        <span className={styles.mono}>{c.claimRef}</span>
                                                        <StatusPill size="sm" tone={c.preselected ? 'good' : 'neutral'} title="Match confidence from the identity scorer (0-100)">
                                                            {c.score}% confidence
                                                        </StatusPill>
                                                    </div>
                                                    <div className={styles.muted}>{describeCandidate(c)}</div>
                                                    {c.reasons.length > 0 && <div className={styles.reasons}>Matched on {c.reasons.map((r) => humanise(r)).join(', ')}</div>}
                                                </div>
                                                <Button size="sm" variant={isSelected ? 'primary' : 'neutral'} disabled={busy !== null || isSelected} onClick={() => setSelectedClaim({ claimRef: c.claimRef, from: 'suggestion' })}>
                                                    {isSelected ? 'Selected' : 'Select'}
                                                </Button>
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}

                            <CollapsibleSection title={candidates.length ? 'Choose a different case' : 'Search for the case'} variant="sub" level="h4" defaultOpen={candidates.length === 0}>
                                <ClaimSearch busy={busy !== null} actionLabel="Select" onLink={(claimRef) => setSelectedClaim({ claimRef, from: 'search' })} />
                            </CollapsibleSection>

                            <div className={styles.confirmBar}>
                                <div>
                                    {selectedClaim ? (
                                        <span>
                                            Selected case <span className={styles.mono}>{selectedClaim.claimRef}</span>
                                            <span className={styles.muted}> ({selectedClaim.from === 'search' ? 'from search' : 'from suggestions'}) — not filed yet</span>
                                        </span>
                                    ) : (
                                        <span className={styles.muted}>Select a case to confirm.</span>
                                    )}
                                </div>
                                <div className={styles.confirmActions}>
                                    {selectedClaim && (
                                        <Button size="sm" variant="ghost" onClick={() => setSelectedClaim(null)} disabled={busy !== null}>
                                            Clear
                                        </Button>
                                    )}
                                    <Button variant="primary" onClick={handleConfirm} disabled={!selectedClaim || busy !== null} loading={busy === 'confirm'}>
                                        Confirm case and file
                                    </Button>
                                </div>
                            </div>
                        </section>
                    )}

                    {item.extracted_at && (
                        <CollapsibleSection title="Saved text" meta={`${(item.extracted_text || '').length.toLocaleString()} characters`} variant="sub" level="h4">
                            {pages.length > 0 && (
                                <p className={styles.note}>
                                    {pages.map((p) => `p${p.page}: ${p.source === 'ocr' ? 'OCR' : 'pdf.js'}${p.error ? ' (unreadable)' : ''}`).join(' · ')}
                                </p>
                            )}
                            <CodeBlock maxHeight={420}>{item.extracted_text || 'No text was read from this letter.'}</CodeBlock>
                        </CollapsibleSection>
                    )}
                </div>
            </div>
        </Card>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────

export const CommsDocumentIngestView = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedBatchId = searchParams.get('batch')
    const selectedItemId = searchParams.get('item')

    const [batches, setBatches] = useState<IngestBatch[]>([])
    const [listLoading, setListLoading] = useState(true)
    const [batch, setBatch] = useState<IngestBatchDetail | null>(null)
    const [batchLoading, setBatchLoading] = useState(false)
    const [status, setStatus] = useState<ProcessingStatus | null>(null)
    const [startingProcessing, setStartingProcessing] = useState(false)
    const [processingNote, setProcessingNote] = useState<string | null>(null)

    const [files, setFiles] = useState<File[]>([])
    const [uploading, setUploading] = useState(false)
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const select = useCallback(
        (batchId: string | null, itemId: string | null = null) => {
            const next = new URLSearchParams()
            if (batchId) next.set('batch', batchId)
            if (itemId) next.set('item', itemId)
            setSearchParams(next, { replace: true })
        },
        [setSearchParams]
    )

    const loadBatches = useCallback(async () => {
        try {
            const resp = await api({ url: 'comms/document-ingest/batches', data: {} })
            if (resp.data?.success) setBatches(resp.data.batches || [])
        } catch (err: any) {
            Report.failure('Load failed', errorMessage(err, 'Could not load daily-post uploads.'), 'Okay')
        } finally {
            setListLoading(false)
        }
    }, [])

    const loadBatch = useCallback(async (batchId: string) => {
        try {
            const resp = await api({ url: 'comms/document-ingest/batch', data: { batchId } })
            if (resp.data?.success) setBatch(resp.data.batch)
        } catch (err: any) {
            setBatch(null)
            Report.failure('Load failed', errorMessage(err, 'Could not load this upload.'), 'Okay')
        } finally {
            setBatchLoading(false)
        }
    }, [])

    const loadStatus = useCallback(async () => {
        try {
            const resp = await api({ url: 'comms/document-ingest/status', data: {} })
            if (resp.data?.success) setStatus({ job: resp.data.job, counts: resp.data.counts || {} })
        } catch {
            setStatus(null)
        }
    }, [])

    const refresh = useCallback(async () => {
        await Promise.all([loadBatches(), loadStatus(), selectedBatchId ? loadBatch(selectedBatchId) : Promise.resolve()])
    }, [loadBatches, loadStatus, loadBatch, selectedBatchId])

    useEffect(() => {
        loadBatches()
        loadStatus()
    }, [loadBatches, loadStatus])

    // Reopening the page lands on the newest upload, so work in progress is where it was left.
    useEffect(() => {
        if (!selectedBatchId && batches.length) select(batches[0].id)
    }, [selectedBatchId, batches, select])

    useEffect(() => {
        if (!selectedBatchId) {
            setBatch(null)
            return
        }
        setBatchLoading(true)
        loadBatch(selectedBatchId)
    }, [selectedBatchId, loadBatch])

    const anythingMoving = batches.some(batchIsActive) || (batch ? batchIsActive(batch) || batch.items.some((i) => isInFlight(i.status)) : false)
    const refreshRef = useRef(refresh)
    refreshRef.current = refresh
    useEffect(() => {
        if (!anythingMoving) return
        const timer = setInterval(() => refreshRef.current(), POLL_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [anythingMoving])

    const chooseFiles = (picked: File[]) => {
        if (uploading || !picked.length) return
        setFiles(picked)
        setUploadResult(null)
    }

    const clearFiles = () => {
        setFiles([])
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleUpload = async () => {
        if (!files.length) return
        setUploading(true)
        try {
            const fd = new FormData()
            files.forEach((file) => fd.append('files', file, file.name))
            let result: UploadResult
            try {
                const resp = await apiUpload('comms/document-ingest/upload', fd)
                result = resp.data
            } catch (err: any) {
                // Every file rejected still returns the per-file report.
                if (err?.response?.status === 400 && Array.isArray(err.response.data?.rejected)) result = err.response.data
                else throw err
            }
            setUploadResult(result)
            clearFiles()
            await Promise.all([loadBatches(), loadStatus()])
            if (result.batchId) select(result.batchId)
        } catch (err: any) {
            Report.failure('Upload failed', errorMessage(err, 'Could not upload these files. Nothing was queued.'), 'Okay')
        } finally {
            setUploading(false)
        }
    }

    const handleStartProcessing = async () => {
        setStartingProcessing(true)
        setProcessingNote(null)
        try {
            const resp = await api({ url: 'comms/document-ingest/process', data: {} })
            if (resp.data?.started === false && resp.data.reason) setProcessingNote(PROCESSING_REASON[resp.data.reason] || `Processing did not start (${humanise(resp.data.reason)}).`)
            await refresh()
        } catch (err: any) {
            Report.failure('Could not start', errorMessage(err, 'Could not start daily-post processing.'), 'Okay')
        } finally {
            setStartingProcessing(false)
        }
    }

    const handleRetryItem = async (item: IngestItem) => {
        try {
            await api({ url: 'comms/document-ingest/retry', data: { itemId: item.id } })
            await refresh()
        } catch (err: any) {
            Report.failure('Retry failed', errorMessage(err, 'Could not retry this letter.'), 'Okay')
        }
    }

    const counts = status?.counts || {}
    const queued = counts.queued ?? 0
    const processing = (counts.extracting ?? 0) + (counts.summarising ?? 0)
    const jobRunning = status?.job?.state === 'running' && Boolean(status.job.leaseOwner)
    const uploadStep: BatchWidgetStep = uploading ? 'verifying' : files.length ? 'ready' : 'choose'
    const selectedItem = batch?.items.find((i) => i.id === selectedItemId) || null
    const uploadNote = uploadResult?.processing && !uploadResult.processing.started && uploadResult.processing.reason ? PROCESSING_REASON[uploadResult.processing.reason] || `Processing did not start (${humanise(uploadResult.processing.reason)}).` : null

    return (
        <div className={styles.layout}>
            <aside className={styles.masterColumn}>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.zip,application/pdf,image/jpeg,image/png,image/tiff,application/zip"
                    className={styles.fileInput}
                    onChange={(e) => chooseFiles(Array.from(e.target.files || []))}
                />
                <BatchWidgetCard
                    step={uploadStep}
                    title="Upload daily post"
                    subtitle="Drop or choose LOC PDFs or scans (JPEG, PNG, TIFF), or a ZIP of them. Up to 50 files."
                    sampleFileName="daily-post.pdf"
                    onChoose={() => fileInputRef.current?.click()}
                    onFilesDropped={chooseFiles}
                    chooseAction={
                        <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                            Choose files
                        </Button>
                    }
                    fileName={files.length === 0 ? undefined : files.length === 1 ? files[0].name : `${files.length} files: ${files.map((f) => f.name).join(', ')}`}
                    onClearFile={clearFiles}
                    readyAction={
                        <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                            Upload {files.length === 1 ? 'file' : `${files.length} files`}
                        </Button>
                    }
                    runningTitle="Uploading and checking files…"
                />

                {uploadResult && (
                    <Card
                        padding="sm"
                        muted
                        title="Upload result"
                        actions={
                            <Button size="sm" variant="ghost" onClick={() => setUploadResult(null)}>
                                Dismiss
                            </Button>
                        }
                    >
                        <div className={styles.stack}>
                            <Banner tone={uploadResult.accepted.length ? (uploadResult.rejected.length ? 'warn' : 'good') : 'bad'}>
                                {uploadResult.accepted.length} letter{uploadResult.accepted.length === 1 ? '' : 's'} queued
                                {uploadResult.rejected.length ? `, ${uploadResult.rejected.length} not accepted` : ''}.
                            </Banner>
                            {uploadNote && <Banner tone="warn">{uploadNote}</Banner>}
                            {uploadResult.rejected.length > 0 && (
                                <ul className={styles.rejectList}>
                                    {uploadResult.rejected.map((r, i) => (
                                        <li key={`${r.filename}-${i}`}>
                                            <span className={styles.mono}>{r.filename}</span>
                                            <span className={styles.rowError}>{r.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Card>
                )}

                <Card padding="sm" title="Processing" subtitle={jobRunning ? 'Running' : 'Idle'}>
                    <div className={styles.stack}>
                        <div className={styles.counts}>
                            <span>{queued} queued</span>
                            <span>{processing} reading</span>
                            <span>{counts.review ?? 0} to review</span>
                            <span>{counts.failed ?? 0} failed</span>
                        </div>
                        {processingNote && <Banner tone="warn">{processingNote}</Banner>}
                        {status?.job?.lastError && !jobRunning && <p className={styles.rowError}>Last run: {status.job.lastError}</p>}
                        {queued > 0 && !jobRunning && (
                            <Button size="sm" onClick={handleStartProcessing} loading={startingProcessing} disabled={startingProcessing}>
                                Start processing
                            </Button>
                        )}
                    </div>
                </Card>

                <div className={styles.listHeader}>
                    <h3 className={styles.listTitle}>Uploads</h3>
                    <Button size="sm" variant="ghost" onClick={refresh}>
                        Refresh
                    </Button>
                </div>

                {listLoading ? (
                    <Spinner block text="Loading uploads…" />
                ) : batches.length === 0 ? (
                    <EmptyState bare title="No uploads yet" description="Upload today's post to begin." />
                ) : (
                    <ul className={styles.batchList}>
                        {batches.map((b) => (
                            <li key={b.id}>
                                <button type="button" className={`${styles.batchItem} ${b.id === selectedBatchId ? styles.batchItemActive : ''}`} onClick={() => select(b.id)}>
                                    <span className={styles.batchItemTop}>
                                        <span className={styles.batchItemName}>{formatDateTime(b.created_at)}</span>
                                        <StatusPill size="sm" tone={BATCH_STATUS[b.status].tone} dot>
                                            {BATCH_STATUS[b.status].label}
                                        </StatusPill>
                                    </span>
                                    <span className={styles.batchItemMeta}>
                                        <span>
                                            {b.total_items} letter{b.total_items === 1 ? '' : 's'} · {b.created_by}
                                        </span>
                                        <span>
                                            {b.review_items} to review · {b.failed_items} failed · {b.confirmed_items} filed
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>

            <section className={styles.detailColumn}>
                {!selectedBatchId ? (
                    <EmptyState bare title="Select an upload" description="Pick an upload to follow its letters and review them." />
                ) : batchLoading || !batch ? (
                    <Spinner block text="Loading upload…" />
                ) : (
                    <div className={styles.stack}>
                        <div className={styles.detailHeader}>
                            <div>
                                <h2 className={styles.detailTitle}>Upload of {formatDateTime(batch.created_at)}</h2>
                                <div className={styles.detailSub}>
                                    <StatusPill size="sm" tone={BATCH_STATUS[batch.status].tone} dot>
                                        {BATCH_STATUS[batch.status].label}
                                    </StatusPill>
                                    <span>Uploaded by {batch.created_by}</span>
                                    <span>
                                        {batch.total_items} letters · {batch.queued_items + batch.processing_items} in progress · {batch.review_items} to review · {batch.failed_items} failed · {batch.confirmed_items} filed
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.tableWrapper}>
                            <table className={styles.itemTable}>
                                <thead>
                                    <tr>
                                        <th>Letter</th>
                                        <th>Status</th>
                                        <th>Read by</th>
                                        <th>Category</th>
                                        <th>Case</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {batch.items.map((item) => (
                                        <tr key={item.id} className={item.id === selectedItemId ? styles.itemRowActive : styles.itemRow} onClick={() => select(batch.id, item.id)}>
                                            <td>
                                                <div className={styles.fileCell}>
                                                    <StatusPill size="sm" tone="outline">
                                                        LOC
                                                    </StatusPill>
                                                    <span className={styles.fileName} title={item.original_filename}>
                                                        {item.original_filename}
                                                    </span>
                                                </div>
                                                {item.last_error && (
                                                    <div className={styles.rowError} title={item.last_error}>
                                                        {item.last_error}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <ItemStatusPill item={item} />
                                            </td>
                                            <td>
                                                <SourcePill item={item} />
                                            </td>
                                            <td>{item.category_label || <span className={styles.muted}>—</span>}</td>
                                            <td>{caseSummary(item)}</td>
                                            <td className={styles.rowActions}>
                                                {item.status === 'failed' && (
                                                    <Button
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleRetryItem(item)
                                                        }}
                                                    >
                                                        Retry
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="link" onClick={() => select(batch.id, item.id)}>
                                                    {item.status === 'review' ? 'Review' : 'Open'}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {selectedItem ? (
                            <ReviewWorkspace key={selectedItem.id} listItem={selectedItem} onChanged={refresh} />
                        ) : (
                            <EmptyState bare title="Select a letter" description="Open a letter to see the original, the saved text and summary, and to confirm its case." />
                        )}
                    </div>
                )}
            </section>
        </div>
    )
}
